import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Layers, Loader2, Clock, Sparkles, CheckCircle2 } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

export const CreditPoolCard = () => {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState('');
  const [committing, setCommitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/tenant/billing/credit-pool');
      setData(res.data);
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tierFor = (amt) => {
    const tiers = data?.tiers || [];
    return tiers.find((t) => amt >= t.min_usd) || null;
  };

  const commit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 1000) { toast.error('Minimum committed pool is $1,000'); return; }
    setCommitting(true);
    try {
      const res = await api.post('/api/tenant/billing/credit-pool', { amount_usd: amt });
      if (res.data?.checkout_url) {
        toast.success('Redirecting to secure checkout...');
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Could not start checkout');
        setCommitting(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start credit pool checkout');
      setCommitting(false);
    }
  };

  const previewTier = amount ? tierFor(parseFloat(amount) || 0) : null;
  const previewCredits = (amount && previewTier)
    ? Math.round((parseFloat(amount) || 0) * previewTier.credits_per_usd)
    : null;

  return (
    <Card data-testid="credit-pool-card" className="border-primary/20">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10"><Layers className="h-5 w-5 text-primary" /></div>
          <div>
            <CardTitle>Committed credit pools</CardTitle>
            <CardDescription>Prepay credits at a discount. Pools burn down after your monthly credits and expire in 12 months.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current pool */}
        {data && data.pool_credits > 0 && !data.expired ? (
          <div className="flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="credit-pool-active">
            <div>
              <p className="text-sm font-medium flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" />Active pool</p>
              <p className="text-2xl font-bold mt-0.5">{data.pool_credits.toLocaleString()} <span className="text-sm font-normal text-muted-foreground">Cr</span></p>
            </div>
            <Badge variant="outline" className="text-xs"><Clock className="h-3 w-3 mr-1" />{data.days_remaining} days left</Badge>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="credit-pool-empty">No active pool. Commit credits below to lock in bonus value.</p>
        )}

        {/* Tiers */}
        <div className="grid grid-cols-3 gap-2" data-testid="credit-pool-tiers">
          {(data?.tiers || []).slice().reverse().map((t) => (
            <button key={t.min_usd} onClick={() => setAmount(String(t.min_usd))}
              className="rounded-lg border border-border p-2.5 text-left hover:border-primary/40 transition-colors" data-testid={`pool-tier-${t.min_usd}`}>
              <p className="text-xs text-muted-foreground">${t.min_usd.toLocaleString()}+</p>
              <p className="text-lg font-bold text-primary">{t.discount_pct}%</p>
              <p className="text-[10px] text-muted-foreground">{t.credits_per_usd} Cr / $1</p>
            </button>
          ))}
        </div>

        {/* Commit */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground mb-1 block">Amount (USD)</label>
            <Input type="number" min="1000" step="500" placeholder="5000" value={amount}
              onChange={(e) => setAmount(e.target.value)} data-testid="pool-amount-input" />
          </div>
          <Button onClick={commit} disabled={committing} data-testid="pool-commit-btn">
            {committing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}Commit
          </Button>
        </div>
        {previewCredits && (
          <p className="text-xs text-emerald-500" data-testid="pool-preview">
            ${parseFloat(amount).toLocaleString()} → ~{previewCredits.toLocaleString()} credits at {previewTier.discount_pct}% bonus
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default CreditPoolCard;
